/* eslint-disable */
import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as SortIcon } from '../../assets/icons/sort-icon.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import NoDataFound from '../../assets/images/no-data-found.png';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import HighlightText from '../../shared-functions/HighlightText';
import BatteryLoader from '../battery-loader/BatteryLoader';
import Pagination from '../pagination/Pagination';
import TopHeaderButtons from '../top-header-buttons/TopHeaderButtons';

import CustomTableHeader from './components/TableHeader';

type RowWithId = { id: string; [key: string]: any }; // Ensure id is either string or number

// Define ColumnHeaderProps to be generic and flexible for any data row structure
type ColumnHeaderProperties<T> = {
  label: string;
  sortAllow?: boolean;
  customLabel?: JSX.Element;
  accessorKey: keyof T; // Tied to the keys of the data row (T)
  customData?: (currentRow: T, index: number) => JSX.Element | null; // Custom rendering function for the data
  columnWidth?: number;
  alignDataToRight?: boolean;
  allowColumnResize?: boolean; // By default it will be undefined
  queryParamsKey?: string; // This is used to push a value to search query params
  queryParamsValue?: string; // If not passed, query params will take row id by default
  sidePanelComponent?: JSX.Element;
  hideWhenSidePanelOpen?: boolean; // If true, this row will be hidden when side panel is open
  handleClickExpandView?: (currentRow: any, newTab: boolean) => void; // Function to expand side panel view in full screen
};

interface CustomTableProperties<T extends RowWithId> {
  tableHeight?: string;
  paginate?: boolean;
  loading?: boolean;
  header: ColumnHeaderProperties<T>[]; // Header configuration for the table
  data: T[]; // Array of data representing rows in the table
  currentPage: number;
  handlePageChange: (pageNumber: number) => void;
  handleLimitChange: (changeLimit: number) => void;
  handleSortChange?: (change: any) => void;
  sortData?: {
    sortBy: string;
    sortField: string;
  };
  footerLeft?: JSX.Element;
  filterFunction?: () => void;
  filterClearFunction?: () => void;
  leftTopContent?: JSX.Element;
  rightTopContent?: JSX.Element;
  sizeChangeButtonRequired?: boolean;
  topHeader?: boolean;
  filterAllow?: boolean;
  page?: number;
  totalResults?: number;
  totalPages?: number;
  limit?: number;
  setHeightAutoToTr?: boolean;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading?: boolean;
  filterListToDispatch?: AsyncThunk<any, void, any>;
}

/**
 * CustomTable component
 */
function CustomTable<T extends RowWithId>(properties: CustomTableProperties<T>) {
  const {
    tableHeight,
    paginate,
    loading,
    header,
    data,
    currentPage,
    limit,
    totalPages,
    totalResults,
    handlePageChange,
    handleLimitChange,
    handleSortChange = () => {},
    sortData,
    leftTopContent,
    rightTopContent,
    filterFunction = () => {},
    filterClearFunction = () => {},
    sizeChangeButtonRequired,
    filterAllow,
    topHeader,
    footerLeft,
    setHeightAutoToTr,
    filters,
    filtersToDispatch,
    filterListLoading,
    filterListToDispatch,
  } = properties;

  const [textToHighlight, setTextToHighlight] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    header.reduce(
      (accumulator, col) => {
        accumulator[col.accessorKey.toString()] = col.columnWidth || 140; // Default width
        return accumulator;
      },
      {} as { [key: string]: number },
    ),
  );
  const [tableSize, setTableSize] = useState<'small' | 'medium' | 'large'>('small');
  const [widthOfContainers, setWidthOfContainers] = useState({
    left: 100, // percentage
    right: 0,
    applyTransitionEffect: true,
  });

  /**
   * Function to handle page number change
   * @param pageNumber
   */
  const handlePageChangeInTable = (pageNumber: number) => {
    if (!loading) {
      handlePageChange(pageNumber);
    }
  };

  /**
   * Function to handle limit change
   * @param pageNumber
   */
  const handleLimitChangeInTable = (changeLimit: number) => {
    if (!loading) {
      handleLimitChange(changeLimit);
    }
  };

  /**
   * Function to handle sort field
   * @param pageNumber
   */
  const handleSortFieldTable = (changeLimit: string) => {
    if (!loading) {
      handleSortChange({
        ...sortData,
        sortField: changeLimit,
        sortBy: sortData?.sortField === changeLimit && sortData.sortBy === 'asc' ? 'desc' : 'asc',
      });
    }
  };

  /**
   * Function to handle table size change
   */
  const handleChangeTableSize = () => {
    if (tableSize === 'small') {
      setTableSize('medium');
    } else if (tableSize === 'medium') {
      setTableSize('large');
    } else if (tableSize === 'large') {
      setTableSize('small');
    }
  };

  const containerReference = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  /**
   * handleMouseDown
   * @param event
   * @param key
   */
  const handleColumnMouseDown = (event: React.MouseEvent, key: string) => {
    const startX = event.clientX;
    // eslint-disable-next-line no-template-curly-in-string
    const startWidth = Number(columnWidths[key]);

    /**
     * handleMouseMove
     * @param event
     * @param key
     */
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const columnMovement = moveEvent.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + columnMovement, 40), 1000); // Ensure a minimum width of 40px and maximum width of 1000px

      setColumnWidths({
        ...columnWidths,
        [key]: newWidth,
      });
    };

    /**
     * handleMouseUp
     * @param event
     * @param key
     */
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Function to handle mouse move event
   */
  const handleMouseDown = () => {
    isDragging.current = true;
  };

  /**
   * Function to handle mouse move event
   * @param e
   * @returns
   */
  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging.current || !containerReference.current) return;

    const containerWidth = containerReference.current.offsetWidth;

    const offsetX = event.clientX - containerReference.current.getBoundingClientRect().left;

    const left = (offsetX / containerWidth) * 100;
    const right = 100 - left;

    if (left > 5 && right > 5) {
      setWidthOfContainers({ left, right, applyTransitionEffect: false });
    }
  };

  /**
   * Function to handle mouse move event
   */
  const handleMouseUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    if (filters?.length) {
      const searchBy = filters.find((eachFilter) => eachFilter.searchBy);
      setTextToHighlight(searchBy?.value?.toString() || null);
    }
  }, [filters]);

  // Code to handle params related functions
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [sidePanelDetails, setSidePanelDetails] = useState({
    jsxElement: <div />,
    enableExpandFullScreen: false,
  });

  /**
   * Add or update a specific param, while keeping others
   * @param key
   * @param value
   */
  const updateQueryParameter = (key: string, value: string) => {
    const parameters = new URLSearchParams(); // start fresh, no existing params
    parameters.set(key, value);
    setSearchParameters(parameters);
  };

  /**
   * Function to handle side panel expand view
   */
  const handleSidePanelExpand = (newTab: boolean) => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const activeSidePanelHeader = header.find(
      (eachHeader) => eachHeader.queryParamsKey === Object.keys(parametersObject)[0],
    );

    if (activeSidePanelHeader?.handleClickExpandView) {
      const key = Object.keys(parametersObject)[0];
      const value = parametersObject[key];
      const item = data.find((object) => object.id === value);

      activeSidePanelHeader.handleClickExpandView(item || null, newTab);
    }
  };

  useEffect(() => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const jsxElementToShow = header.find(
      (eachHeader) => eachHeader.queryParamsKey === Object.keys(parametersObject)[0],
    );

    if (parametersObject && jsxElementToShow) {
      setWidthOfContainers({
        left: parameters?.size === 1 ? 60 : 100,
        right: parameters?.size === 1 ? 40 : 0,
        applyTransitionEffect: true,
      });

      setSidePanelDetails({
        jsxElement: jsxElementToShow.sidePanelComponent || <div>Something went wrong</div>,
        enableExpandFullScreen: Boolean(jsxElementToShow.handleClickExpandView),
      }); // If required, need to show the proper error message
    }

    if (parameters?.size === 0) {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [searchParameters]);

  /**
   * Function to close side-panel
   */
  const handleCloseSidePanel = () => {
    setSearchParameters({});
    setWidthOfContainers({
      left: 100,
      right: 0,
      applyTransitionEffect: true,
    });
  };

  // useEffect to handle side-panel open and close
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className="custom-table-wrap">
      {/* <div style={{ display: 'none' }}> */}
      {topHeader ? (
        <TopHeaderButtons
          filterFunction={filterFunction}
          filterClearFunction={filterClearFunction}
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
          filterAllow={filterAllow}
        />
      ) : null}
      {filters && filterListToDispatch ? (
        <CustomTableHeader
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filters={filters}
          filtersToDispatch={filtersToDispatch}
          filterListToDispatch={filterListToDispatch}
          filterListLoading={filterListLoading || false}
        />
      ) : null}
      <div className="custom-table-with-side-panel" ref={containerReference}>
        <div
          className="custom-table"
          style={
            {
              '--table-height': tableHeight,
              width: `${widthOfContainers.left}%`,
              transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
            } as React.CSSProperties
          }>
          <table className={`table-container ${tableSize}`}>
            <thead className="table-header-wrap">
              <tr className="table-header-container">
                {header
                  .filter((col) => !(col.hideWhenSidePanelOpen && widthOfContainers.right > 0))
                  .map((col, index) => (
                    <th
                      className="table-header"
                      key={col.accessorKey as string}
                      style={{
                        minWidth: `${Math.max(columnWidths[col.accessorKey.toString()], 40)}px`,
                        width: `${columnWidths[col.accessorKey.toString()]}px`,
                        flex: `${columnWidths[col.accessorKey.toString()]} 0 auto`,
                      }}>
                      <p>{col.customLabel ? col.customLabel : col.label}</p>
                      {col.sortAllow ? (
                        <div
                          onClick={() => handleSortFieldTable(col.accessorKey as string)}
                          role="presentation">
                          <SortIcon />
                        </div>
                      ) : null}
                      {index !== header.length - 1 && col?.allowColumnResize !== false && (
                        <div
                          className="table-column-resizer"
                          onMouseDown={(event) =>
                            handleColumnMouseDown(event, col.accessorKey.toString())
                          }
                          role="presentation"
                        />
                      )}
                    </th>
                  ))}
              </tr>
            </thead>
            {(() => {
              let tableView = null;
              if (loading) {
                tableView = (
                  <div className="table-loading">
                    <BatteryLoader />
                  </div>
                );
              } else if (data?.length > 0 && !loading) {
                tableView = (
                  <tbody className="table-body">
                    {data.map((row, index) => (
                      <tr
                        className="table-row"
                        key={row.id}
                        style={
                          {
                            height: setHeightAutoToTr && 'auto',
                            '--header-length': header?.length || 1,
                          } as React.CSSProperties
                        }>
                        {header
                          .filter(
                            (col) => !(col.hideWhenSidePanelOpen && widthOfContainers.right > 0),
                          )
                          .map((col) => (
                            <td
                              className="table-row-data"
                              key={col.accessorKey as string}
                              style={{
                                padding: setHeightAutoToTr ? '5px 5px 5px 5px' : '',
                                minWidth: `${Math.max(columnWidths[col.accessorKey.toString()], 40)}px`,
                                width: `${columnWidths[col.accessorKey.toString()]}px`,
                                flex: `${columnWidths[col.accessorKey.toString()]} 0 auto`,
                              }}>
                              {col.customData ? (
                                col.customData(row, index)
                              ) : (
                                // Use type assertion here to indicate that row[col.accessorKey] is valid for rendering
                                <p
                                  className={`table-row-each-data ${col?.queryParamsKey && col?.sidePanelComponent ? 'hyperlink' : ''}`}
                                  role="presentation"
                                  onClick={() => {
                                    if (col?.queryParamsKey && col?.sidePanelComponent) {
                                      const queryValue = col.queryParamsValue
                                        ? row[col.queryParamsValue]
                                        : undefined;
                                      updateQueryParameter(
                                        col.queryParamsKey.toString(),
                                        queryValue || row.id,
                                      );
                                    }
                                  }}>
                                  {HighlightText(
                                    textToHighlight || null,
                                    row[col.accessorKey]?.toString() || '', // Empty quotes '' -> To avoid showing undefined
                                  )}
                                </p>
                              )}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                );
              } else if (data?.length === 0 && !loading) {
                tableView = (
                  <div className="table-no-data-wrap">
                    <img src={NoDataFound} alt="" className="no-data-found-image" />
                    <div className="no-data-found-details">
                      <h3>🔋 Oops! Nothing here yet 🔋</h3>
                      <p>
                        It looks like there&apos;s no data to show right now. Don&apos;t worry—it
                        could just mean things are charging up behind the scenes!
                      </p>
                    </div>
                  </div>
                );
              }
              return tableView;
            })()}
          </table>
        </div>
        {widthOfContainers.right > 0 ? (
          <div className="adjustable-divider" role="presentation" onMouseDown={handleMouseDown} />
        ) : (
          <div />
        )}
        {widthOfContainers.right > 0 ? (
          <div
            className="custom-table-side-panel"
            style={{
              width: `${widthOfContainers.right}%`,
              transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
            }}>
            <div className="side-panel-header">
              <div
                className={`side-panel-header-left ${sidePanelDetails.enableExpandFullScreen ? 'active' : 'in-active'}`}>
                <span role="presentation" onClick={() => handleSidePanelExpand(false)}>
                  <ExpandIcon />
                </span>
                <span role="presentation" onClick={() => handleSidePanelExpand(true)}>
                  <NewTabIcon />
                </span>
              </div>
              <span role="presentation" onClick={handleCloseSidePanel}>
                <CloseIcon />
              </span>
            </div>
            <div className="side-panel-content">{sidePanelDetails.jsxElement}</div>
          </div>
        ) : (
          <div />
        )}
      </div>
      <div className="custom-table-footer">
        <div className="footer-left">{footerLeft}</div>
        <div className="footer-right">
          {paginate ? (
            <Pagination
              activePage={currentPage}
              handlePageChange={handlePageChangeInTable}
              handleLimitChange={handleLimitChangeInTable}
              limit={limit}
              size={totalPages}
              totalResults={totalResults}
            />
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}

CustomTable.defaultProps = {
  tableHeight: '100%',
  paginate: false,
  footerLeft: null,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  limit: 0,
  sizeChangeButtonRequired: true,
  topHeader: true,
  /**
   * Filter function
   */
  filterFunction: () => {},
  /**
   * Filter Clear function
   */
  filterClearFunction: () => {},
  leftTopContent: null,
  rightTopContent: null,
  loading: false,
  filterAllow: true,
};

export default CustomTable;
